/** English strings (single `en` namespace). Keys are grouped by surface. */
export const en = {
  brand: { name: 'Title21', product: 'Support Desk' },
  common: {
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving…',
    create: 'Create',
    delete: 'Delete',
    edit: 'Edit',
    close: 'Close',
    dismiss: 'Dismiss',
    retry: 'Retry',
    loading: 'Loading…',
    none: 'None',
    any: 'any',
    all: 'all',
    yes: 'Yes',
    no: 'No',
    optional: 'optional',
    required: 'Required',
    select: 'Select…',
    clear: 'Clear',
    error: 'Something went wrong: {{message}}',
    notAvailable: 'Not available yet: the API does not serve this endpoint.',
    forbidden: 'You do not have permission to see this.',
  },
  nav: {
    queues: 'Queues',
    byProduct: 'By product',
    admin: 'Admin',
    allOpen: 'All open',
    myOrgTickets: 'Open tickets',
    assignedToMe: 'Assigned to me',
    slaAtRisk: 'SLA at risk',
    gxpImpacting: 'GxP-impacting',
    escalated: 'Escalated to engineering',
    analytics: 'Analytics',
    slaPolicies: 'SLA policies & routing',
    catalogProducts: 'Products',
    catalogClients: 'Clients & tenants',
    signOut: 'Sign out',
    tickets: 'Tickets',
    logTicket: 'Log ticket',
    newTicket: 'New ticket',
  },
  products: {
    cgt: 'CGT',
    eqms: 'eQMS · Quality',
    smartQc: 'Smart-QC',
    documentControl: 'Document Control',
    training: 'Training',
    integrations: 'Integrations',
    platform: 'Platform & Access',
  },
  user: {
    agent: 'Agent',
    superAdmin: 'Administrator',
    customer: 'Customer',
    tier: 'Tier {{tier}}',
  },
  realtime: {
    mention: 'You were mentioned on {{key}}',
    aTicket: 'a ticket',
    slaWarning: 'SLA warning on {{key}}',
  },
  queue: {
    eyebrow: 'Support desk · All customers · All products',
    customerEyebrow: 'Support · {{customer}}',
    customerEyebrowFallback: 'Support · Your organisation',
    title: 'All open tickets',
    titles: {
      all: 'All open tickets',
      mine: 'Assigned to me',
      sla_risk: 'SLA at risk',
      gxp: 'GxP-impacting tickets',
      escalated: 'Escalated to engineering',
    },
    customerTitle: 'Your tickets',
    productTitle: '{{product}} tickets',
    searchPlaceholder: 'Search ticket, customer, record…',
    searchLabel: 'Search tickets',
    newTicket: 'New ticket',
    kpi: {
      open: 'Open',
      critical: 'Critical (P1)',
      slaMet: 'SLA met · 30 days',
      medianFirstResponse: 'Median first response',
      escalated: 'Escalated to engineering',
      analyticsOnly: 'Available with Analytics',
    },
    chips: {
      all: 'All',
      priority: 'Priority',
      product: 'Product',
      type: 'Type',
      customer: 'Customer',
      tenant: 'Tenant',
      clear: 'Clear',
    },
    sortedBySla: 'Sorted by SLA remaining',
    columns: {
      id: 'ID',
      subject: 'Subject',
      customer: 'Customer',
      product: 'Product',
      status: 'Status',
      assignee: 'Assignee',
      sla: 'SLA',
    },
    unassigned: 'Unassigned',
    showing: 'Showing {{count}} of {{total}} · every change is captured in the Part 11 audit trail',
    nextPage: 'Next page →',
    previousPage: '← Previous',
    empty: 'No tickets match these filters.',
    emptyQueue: 'Nothing in this queue. Nice work.',
    clearFilters: 'Clear filters',
    loadError: 'The queue could not be loaded: {{message}}',
    slaPaused: 'SLA clock paused',
    slaPausedLabel: 'Paused',
    slaPausedLeft: 'SLA clock paused · {{time}} left',
    slaBreached: 'SLA breached',
  },
  newTicket: {
    title: 'New ticket',
    description: 'Desk-entered ticket. The reporter is a contact at the customer.',
    customer: 'Customer',
    environment: 'Environment',
    noEnvironment: 'No environment',
    reporter: 'Reporter',
    tenant: 'Tenant',
    product: 'Product',
    component: 'Component',
    noComponent: 'No component',
    type: 'Type',
    priority: 'Priority',
    slaHint: '{{policy}} · first response {{fr}}, resolution {{res}}',
    subject: 'Subject',
    descriptionLabel: 'Description',
    linkedRecord: 'Linked record',
    addLinkedRecord: 'Add a linked record',
    submit: 'Create ticket',
    submitting: 'Creating…',
    pickCustomerFirst: 'Pick a customer first',
    pickProductFirst: 'Pick a product first',
    fullForm: 'Open the full form',
    fullFormHint: 'Phone call, attachments or an internal note? Use the full form.',
    errors: {
      customer: 'Choose the customer.',
      reporter: 'Choose who reported it.',
      product: 'Choose the product.',
      subject: 'Add a subject.',
      description: 'Describe the problem.',
      record: 'A linked record needs a type and a key.',
    },
  },
  manualTicket: {
    eyebrowAgent: 'Tickets · Desk intake',
    eyebrowCustomer: 'Tickets · {{customer}}',
    titleAgent: 'Log a ticket',
    titleCustomer: 'New ticket',
    introAgent: 'For a request that came in by phone, email, Teams or in person. Every field is captured in the Part 11 audit trail.',
    introCustomer: 'Tell us what happened. Our support team is notified as soon as you submit.',
    backToQueue: '← Back to the queue',
    quickCreate: 'Quick create',
    sections: {
      reporter: 'Who reported it',
      intake: 'How it came in',
      about: 'What it is about',
      gxp: 'GxP and records',
      people: 'Owner and watchers',
      attachments: 'Attachments',
      note: 'Opening internal note',
    },
    customer: 'Customer',
    environment: 'Environment',
    noEnvironment: 'Not environment-specific',
    pickCustomerFirst: 'Pick a customer first',
    pickProductFirst: 'Pick a product first',
    reporterMode: 'Reporter',
    existingContact: 'Existing contact',
    newContact: 'New contact',
    contact: 'Contact',
    contactName: 'Name',
    contactEmail: 'Email',
    contactRole: 'Role',
    contactRolePlaceholder: 'QA Release Officer',
    newContactHelp: 'Matched by email if this contact already exists, otherwise created for {{customer}}.',
    newContactRoleNote: 'Saved on the new contact.',
    tenant: 'Tenant',
    tenantHelp: 'The client’s branch or site. Defaults to the reporter’s.',
    tenantFromReporter: 'From the reporter',
    tenantFromReporterNamed: 'From the reporter ({{tenant}})',
    noTenants: 'This customer has no tenants.',
    receivedVia: 'Received via',
    via: {
      phone: 'Phone',
      email: 'Email',
      teams: 'Microsoft Teams',
      in_person: 'In person',
      other: 'Other',
    },
    receivedAt: 'Received at',
    receivedAtHelp: 'Your local time ({{zone}}).',
    product: 'Product',
    component: 'Component',
    noComponent: 'No component',
    type: 'Type',
    priority: 'Priority',
    slaHint: '{{policy}}: first response {{fr}}, resolution {{res}}{{calendar}}',
    slaHintCalendar: ' · {{calendar}}',
    slaHintDefault: 'The SLA clock starts when the ticket is created; the policy is chosen by priority, customer and product.',
    subject: 'Subject',
    subjectPlaceholder: 'One line a colleague would understand at a glance',
    description: 'Description',
    descriptionPlaceholderAgent: 'What the caller described, in their words: what happened, when, which records, and what they already tried.',
    descriptionPlaceholderCustomer: 'What happened, when, which records are affected, and what you already tried.',
    gxpImpact: 'GxP impact',
    gxp: {
      unassessed: 'Not assessed yet',
      none: 'None',
      assessed_no_risk: 'Assessed · no product risk',
      yes: 'Yes — GxP impact',
    },
    gxpNote: 'GxP assessment note',
    gxpNotePlaceholder: 'Why: which records, product or patients could be affected, and who concurred.',
    formRef: 'Form ref',
    formRefPlaceholder: 'FORM-REL-03',
    formVersion: 'Form version',
    formVersionPlaceholder: 'v5.2',
    recordType: 'Linked record type',
    recordTypeNone: 'No linked record',
    recordTypeOther: 'Other (free text)',
    recordTypeCustom: 'Record type',
    recordKey: 'Record key',
    recordKeyValid: 'Matches the {{type}} format',
    recordKeyUnchecked: 'No format on file for this type; saved as typed',
    recordKeyInvalid: 'Doesn’t match the {{type}} format ({{example}})',
    assignee: 'Assignee',
    unassigned: 'Unassigned (routing rules may assign it)',
    watchers: 'Watchers',
    watchersHelp: 'Contacts at the customer who get updates.',
    noWatchers: 'No other contacts at this customer.',
    attachmentsHelp: 'Uploaded after the ticket is created; customer-visible files are attached to the description.',
    addFiles: 'Add files',
    removeFile: 'Remove {{name}}',
    internalOnly: 'Internal only',
    note: 'Internal note',
    notePlaceholder: 'Context for the team: call-back number, what you already checked. Use @Name to mention an agent.',
    noteHelp: 'Never shown to the customer.',
    summary: 'On submit',
    willRun: {
      create: 'Create the ticket',
      attachments: 'Upload {{count}} file(s)',
      note: 'Post the internal note',
    },
    submit: 'Create ticket',
    submitting: 'Working…',
    cancel: 'Cancel',
    progressTitle: 'Progress',
    step: {
      pending: 'Waiting',
      running: 'In progress',
      done: 'Done',
      failed: 'Failed',
    },
    createFailed: 'The ticket was not created: {{message}}',
    partial: '{{key}} was created, but some steps failed. Fix them from the ticket.',
    openTicket: 'Open {{key}}',
    validationSummary: 'Check the highlighted fields.',
    errors: {
      required: 'Required.',
      email: 'Enter a valid email address.',
      future: 'That is in the future.',
      invalid_date: 'Enter a date and time.',
      too_long: 'Too long.',
      record_key: 'This key doesn’t match the record type’s format.',
      gxp_note: 'Explain the assessment.',
      form_version: 'Add the form ref too.',
    },
    pending: {
      title: '{{count}} attachment(s) did not upload',
      item: '{{name}} · {{error}}',
      retry: 'Retry upload',
      retrying: 'Uploading…',
    },
  },
  ticket: {
    eyebrow: 'Ticket',
    title: 'Ticket {{id}}',
    breadcrumbAll: 'All open',
    opened: 'Opened {{time}} by {{name}}',
    openedWithRole: 'Opened {{time}} by {{name}}, {{role}}',
    resolvedBy: 'resolved {{time}} by {{name}}',
    productType: '{{product}} · {{type}}',
    followUpOf: 'Follow-up of {{key}}',
    duplicateOf: 'Duplicate of {{key}}',
    notFound: 'Ticket {{id}} was not found, or you do not have access to it.',
    loadError: 'The ticket could not be loaded: {{message}}',
    actions: {
      escalate: 'Escalate to deviation',
      resolve: 'Resolve',
      export: 'Export thread (PDF)',
      exporting: 'Exporting…',
      reopen: 'Reopen',
    },
    tabs: {
      conversation: 'Conversation',
      full: 'Full thread',
      customer: 'Customer replies only',
      notes: 'Internal notes',
      audit: 'Audit trail',
      attachments: 'Attachments',
      withCount: '{{label}} · {{count}}',
    },
    legend: {
      customer: 'Customer',
      agent: 'Title21 agent',
      note: 'Internal note · hidden from customer',
      system: 'System event',
      systemSample: '00:00',
    },
    message: {
      via: 'via {{channel}}',
      channel: { portal: 'portal', email: 'email' },
      firstResponse: 'first response in {{duration}}',
      edited: 'edited',
      internalHeader: 'Internal · not visible to customer',
      download: 'Download {{name}}',
      empty: 'No messages yet.',
      emptyNotes: 'No internal notes yet.',
      loadError: 'Messages could not be loaded: {{message}}',
    },
    resolvedBlock: {
      heading: 'Resolved',
      by: 'Resolved by',
      reason: 'Reason',
      when: 'Date & time',
      category: 'Category: {{category}}',
      rootCauseRecorded: 'Root cause recorded',
      deviation: 'Deviation: {{decision}}',
      summary: 'Resolution summary',
    },
    csat: {
      rated: '{{name}} rated this ticket {{rating}} / 5',
      someone: 'The customer',
    },
    closed: {
      resolved: 'This ticket is resolved. A customer reply within 7 days reopens it automatically.',
      closed: 'This ticket is closed. A new reply opens a linked follow-up ticket.',
      addNote: 'Add internal note',
      reply: 'Reply',
    },
    rail: {
      linkedRecord: 'Linked record · {{product}}',
      linkedRecordPlain: 'Linked record',
      noRecord: 'No record linked.',
      openInProduct: 'Open in product',
      asOf: 'as of {{time}}',
      details: 'Details',
      status: 'Status',
      editStatus: 'Change status',
      receivedVia: 'Received via',
      gxpNote: 'GxP note',
      assignee: 'Assignee',
      customer: 'Customer',
      tenant: 'Tenant',
      noTenant: 'No tenant',
      product: 'Product',
      formVersion: 'Form / version',
      gxpImpact: 'GxP impact',
      sla: 'SLA',
      slaWithPolicy: 'SLA ({{priority}}, {{target}})',
      deviation: 'Deviation',
      noneLinked: 'None linked',
      unassigned: 'Unassigned',
      remaining: '{{time}} remaining',
      overdue: '{{time}} overdue',
      met: 'Met · {{time}} to restore',
      pausedLeft: 'Paused · {{time}} left',
      pausedOnly: 'Paused',
      editAssignee: 'Change assignee',
      editGxp: 'Change GxP impact',
      editTenant: 'Change tenant',
      conflict: 'Someone else changed this ticket. It has been refreshed; check the values and try again.',
      saveError: 'The change was not saved: {{message}}',
    },
    gxp: {
      unassessed: 'Not assessed',
      none: 'None',
      assessed_no_risk: 'Yes · assessed, no product risk',
      yes: 'Yes — GxP impact',
    },
    deviation: {
      not_required: 'Not required',
      raised: 'Raised',
      pending: 'Pending decision',
      raisedRef: 'Raised · {{ref}}',
    },
    category: {
      configuration_restored: 'Configuration restored',
      code_fix: 'Code fix',
      user_error_training: 'User error / training',
      data_corrected: 'Data corrected',
      vendor_issue: 'Vendor issue',
      duplicate: 'Duplicate',
      other: 'Other',
    },
  },
  composer: {
    replyPlaceholder: 'Reply to {{name}}, or @mention a teammate…',
    replyPlaceholderGeneric: 'Write a reply, or @mention a teammate…',
    customerPlaceholder: 'Write a reply…',
    notePlaceholder: 'Internal note, hidden from the customer. @mention a teammate…',
    attach: 'Attach',
    insertRecord: 'Insert record link',
    internalNote: 'Internal note',
    reply: 'Reply',
    hint: 'Closing requires a reason and a resolution summary',
    send: 'Send',
    sending: 'Sending…',
    addNote: 'Add note',
    uploading: 'Uploading {{name}}…',
    removeFile: 'Remove {{name}}',
    mentionNoMatch: 'No teammate matches',
    mentionList: 'Teammates',
    sendError: 'Not sent: {{message}}',
    reopened: 'The reply reopened the ticket.',
    followUp: 'The ticket was closed, so the reply opened follow-up {{key}}.',
    noteMode: 'Writing an internal note',
  },
  recordLink: {
    title: 'Insert record link',
    description: 'Link a record in any Title21 product. Without a provider it is a plain reference.',
    product: 'Product',
    type: 'Record type',
    typePlaceholder: 'din, capa, sop…',
    key: 'Record key',
    keyPlaceholder: 'W1234 26 004517',
    url: 'URL',
    insertAsText: 'Also insert the key into the message',
    submit: 'Link record',
    submitting: 'Linking…',
    notAvailable: 'Linking is not available yet; the key was inserted into the message.',
  },
  escalate: {
    title: 'Escalate to deviation',
    description: 'Record the deviation decision for this ticket. It is audit-trailed.',
    decision: 'Decision',
    ref: 'Deviation reference',
    refPlaceholder: 'DEV-2026-0042',
    submit: 'Escalate',
    submitting: 'Escalating…',
    refRequired: 'A raised deviation needs its reference.',
  },
  resolve: {
    title: 'Resolve {{key}}',
    description: 'The summary is sent to the customer as the final reply. Everything here is audit-trailed.',
    summary: 'Resolution summary',
    summaryHelp: 'Sent to the customer as the final reply.',
    templateCause: 'Cause',
    templateFix: 'Fix',
    templatePrevention: 'Prevention',
    category: 'Category',
    duplicateOf: 'Duplicate of (ticket number)',
    duplicatePlaceholder: 'TKT-04801',
    rootCause: 'Root cause',
    rootCauseRequired: 'Root cause (required for a defect)',
    gxpImpact: 'GxP impact',
    deviationDecision: 'Deviation decision',
    deviationRef: 'Deviation reference',
    reason: 'Reason',
    reasonPlaceholder: 'Short audited reason, e.g. Verified with reporter',
    submit: 'Resolve ticket',
    submitting: 'Resolving…',
    serverProblems: 'The server refused the resolution:',
    duplicateNotFound: 'Ticket {{key}} was not found.',
    errors: {
      summary: 'Write the resolution summary (cause, fix, prevention).',
      category: 'Choose a category.',
      rootCause: 'A defect needs its root cause.',
      gxpImpact: 'Assess the GxP impact.',
      deviationRef: 'A raised deviation needs its reference.',
      duplicateOf: 'Enter the ticket number it duplicates.',
      reason: 'Give a short reason.',
    },
  },
  reopen: {
    title: 'Reopen {{key}}',
    description: 'The ticket returns to In progress and the SLA clock resumes. The reason is audit-trailed.',
    reason: 'Reason',
    reasonPlaceholder: 'Why is this being reopened?',
    submit: 'Reopen ticket',
    submitting: 'Reopening…',
    required: 'Give a reason.',
  },
  exportPdf: {
    title: 'Export thread (PDF)',
    description: 'The customer copy never contains internal notes. The footer carries the audit chain head hash.',
    audienceCustomer: 'Customer copy',
    audienceInternal: 'Internal copy (with notes)',
    submit: 'Download PDF',
    thread: 'Thread',
    resolution: 'Resolution',
    attachments: 'Attachments',
    internalNote: 'INTERNAL NOTE',
    resolvedBy: 'Resolved by',
    resolvedAt: 'Resolved at',
    category: 'Category',
    rootCause: 'Root cause',
    deviation: 'Deviation decision',
    reason: 'Reason',
    summary: 'Summary',
    audience: 'Audience: {{audience}}',
    audienceName: { customer: 'customer', internal: 'internal' },
    auditHead: 'Audit chain head {{hash}} ({{state}})',
    verified: 'verified',
    unverified: 'NOT verified',
    generated: 'Generated {{at}} by {{by}}',
    opened: 'Opened {{when}} by {{by}}',
  },
  audit: {
    verify: 'Verify chain',
    verifying: 'Verifying…',
    valid: 'Chain intact · {{length}} entries · head {{hash}}',
    anchored: 'Anchored {{day}}',
    invalid: 'Chain broken at entry {{seq}}: {{reason}}',
    columns: { seq: '#', at: 'When', event: 'Event', field: 'Field', change: 'Change', actor: 'Actor', hash: 'Hash' },
    empty: 'No audit entries.',
    internal: 'internal',
    loadError: 'The audit trail could not be loaded: {{message}}',
  },
  attachmentsTab: {
    empty: 'No attachments.',
    columns: { name: 'File', size: 'Size', visibility: 'Visibility', uploaded: 'Uploaded' },
    internal: 'Internal',
    customer: 'Customer',
  },
  analytics: { eyebrow: 'Admin · All customers · All products', title: 'Support analytics' },
  sla: { eyebrow: 'Admin', title: 'SLA policies & routing' },
  catalog: {
    eyebrow: 'Admin · Catalog',
    superAdminOnly: 'Only superadmins can manage the catalog.',
    back: 'Back',
    status: { active: 'Active', inactive: 'Inactive', retired: 'Retired', suspended: 'Suspended' },
    kinds: { production: 'Production', validation: 'Validation', test: 'Test', training: 'Training', sandbox: 'Sandbox' },
    normalizers: { none: 'None (trim only)', upper: 'Upper-case', strip_spaces_upper: 'Strip spaces & dashes, upper-case' },
    retire: 'Retire',
    reactivate: 'Reactivate',
    deactivate: 'Deactivate',
    activate: 'Activate',
    rename: 'Rename',
    validated: 'Validated',
    notValidated: 'Not validated',
    products: {
      title: 'Products',
      new: 'New product',
      createTitle: 'New product',
      empty: 'No products yet.',
      cols: { name: 'Name', key: 'Key', components: 'Components', sla: 'SLA target', tickets: 'Tickets', status: 'Status' },
      tabs: { details: 'Details', components: 'Components', patterns: 'Record key patterns' },
      name: 'Name',
      key: 'Key',
      keyHelp: 'Lower-case, digits and _ — used in URLs and routing rules.',
      keyLocked: 'Locked: tickets reference this product.',
      description: 'Description',
      slaTarget: 'SLA target (%)',
      active: 'Active — offered when creating tickets',
      inactiveNote: 'Inactive products are hidden from ticket pickers; existing tickets keep them.',
      saved: 'Saved',
      ticketCount: '{{count}} tickets',
    },
    components: {
      add: 'Add component',
      namePlaceholder: 'Component name, e.g. Release labels',
      keyPlaceholder: 'key',
      empty: 'No components yet.',
      cols: { name: 'Name', key: 'Key', tickets: 'Tickets', status: 'Status' },
      deleteTitle: 'Delete component {{name}}?',
      deleteBody: 'Only components no ticket uses can be deleted. Recorded in the admin audit.',
    },
    patterns: {
      add: 'New pattern',
      createTitle: 'New record key pattern',
      editTitle: 'Edit record key pattern',
      empty: 'No record key patterns yet.',
      cols: { type: 'Type', regex: 'Regex', normalizer: 'Normalizer', url: 'URL template' },
      type: 'Record type',
      typeHelp: 'e.g. din, capa, sop',
      regex: 'Regular expression',
      sample: 'Sample key (must match)',
      normalizer: 'Normalizer',
      urlTemplate: 'URL template (optional)',
      urlHelp: 'https URL with {key}, e.g. https://cgt.example/records/din/{key}',
      tester: 'Live tester',
      testKey: 'Try a key',
      matches: 'Matches',
      noMatch: 'Does not match',
      badRegex: 'Regex does not compile: {{message}}',
      normalized: 'Normalized (search key)',
      url: 'Record URL',
      noUrl: 'No URL template',
      deleteTitle: 'Delete the {{name}} pattern?',
      deleteBody: 'Linked records on tickets keep their keys. Recorded in the admin audit.',
    },
    clients: {
      title: 'Clients & tenants',
      new: 'New client',
      createTitle: 'New client',
      search: 'Search clients…',
      empty: 'No clients match.',
      cols: { name: 'Name', slug: 'Slug', timeZone: 'Time zone', tenants: 'Tenants', environments: 'Environments', status: 'Status' },
      tabs: { details: 'Details', tenants: 'Tenants', environments: 'Environments' },
      name: 'Name',
      slug: 'Slug',
      slugHelp: 'Lower-case letters, digits and -',
      timeZone: 'Time zone',
      timeZoneHelp: 'IANA zone, e.g. America/Chicago. Business-hours SLAs run in it.',
      emailDomains: 'Email domains',
      emailDomainsHelp: 'Inbound email from these domains is matched to this client. Press Enter or comma to add.',
      domainPlaceholder: 'mayo.edu',
      invalidDomains: 'Not a domain: {{list}}',
      removeDomain: 'Remove {{domain}}',
      suspend: 'Suspend client',
      reactivate: 'Reactivate client',
      suspendTitle: 'Suspend {{name}}?',
      suspendBody: 'A suspended client cannot open new tickets. Existing tickets stay workable by agents.',
      reactivateTitle: 'Reactivate {{name}}?',
      reactivateBody: 'The client can open new tickets again.',
      reason: 'Reason (audited)',
      suspendedNote: 'Suspended: no new tickets.',
    },
    environments: {
      add: 'New environment',
      createTitle: 'New environment',
      editTitle: 'Edit environment',
      empty: 'No environments yet.',
      cols: { product: 'Product', name: 'Name', kind: 'Kind', validated: 'Validated', baseUrl: 'Base URL', tenants: 'Tenants', status: 'Status' },
      product: 'Product',
      productHelp: 'Only active products.',
      name: 'Name',
      kind: 'Kind',
      isValidated: 'Validated (GxP) environment',
      baseUrl: 'Base URL (optional)',
      retireTitle: 'Retire environment {{name}}?',
      retireBody: 'Retired environments are hidden from ticket pickers; tickets and tenant links keep them.',
      deleteTitle: 'Delete environment {{name}}?',
      deleteBody: 'Only environments nothing references can be deleted. Recorded in the admin audit.',
    },
    tenants: {
      add: 'New tenant',
      createTitle: 'New tenant',
      editTitle: 'Edit tenant',
      empty: 'No tenants yet. A tenant is a branch or site of the client.',
      cols: { name: 'Name', code: 'Code', timeZone: 'Time zone', location: 'Location', environments: 'Environments (tenant id)', status: 'Status' },
      name: 'Name',
      code: 'Code',
      codeHelp: 'Short upper-case code, unique for the client (ROC, JAX).',
      timeZone: 'Time zone',
      location: 'Location (optional)',
      links: 'Environment links',
      linksHelp: "Which of the client's environments this tenant uses, and its tenant id inside each.",
      addLink: 'Add environment',
      removeLink: 'Remove link',
      environment: 'Environment',
      externalTenantId: 'Tenant id in the product',
      noEnvironments: 'This client has no environments yet — add them on the Environments tab.',
      retireTitle: 'Retire tenant {{name}}?',
      retireBody: 'Retired tenants are hidden from pickers; tickets, contacts and users keep them.',
      deleteTitle: 'Delete tenant {{name}}?',
      deleteBody: 'Only tenants nothing references can be deleted. Recorded in the admin audit.',
    },
    errors: {
      required: 'Required.',
      key: 'Lower-case letters, digits and _, starting with a letter.',
      duplicateKey: 'That key is already used.',
      duplicateType: 'This product already has a pattern of that type.',
      percent: 'A whole number between 0 and 100.',
      regex: 'The regular expression does not compile.',
      sampleNoMatch: 'The sample does not match the regex.',
      keyPlaceholder: 'Must contain {key}.',
      https: 'Must be an https URL.',
      slug: 'Lower-case letters, digits and -.',
      timeZone: 'Not a known IANA time zone.',
      code: 'Upper-case letters, digits, _ or - (max 16).',
      linkEnvironment: 'Choose an environment for every row.',
      linkDuplicate: 'An environment is linked twice.',
    },
  },
  admin: {
    tabs: { policies: 'SLA policies', calendars: 'Calendars', routing: 'Routing rules', audit: 'Admin audit' },
    add: { policies: 'New policy', calendars: 'New calendar', routing: 'New rule' },
    editTitle: { policies: 'Edit SLA policy', calendars: 'Edit calendar', routing: 'Edit routing rule' },
    createTitle: { policies: 'New SLA policy', calendars: 'New business calendar', routing: 'New routing rule' },
    empty: 'Nothing here yet.',
    deleteTitle: 'Delete {{name}}?',
    deleteBody: 'This cannot be undone. The change is recorded in the admin audit.',
    active: 'Active',
    inactive: 'Inactive',
    policy: {
      name: 'Name',
      priority: 'Priority',
      firstResponse: 'First response (minutes)',
      resolution: 'Resolution (minutes)',
      calendar: 'Business calendar',
      warnAt: 'Warn at (% of budget)',
      pauseStatuses: 'Clock pauses in',
      customer: 'Customer (optional)',
      product: 'Product (optional)',
      anyCustomer: 'Any customer',
      anyProduct: 'Any product',
      targetsHeader: 'First response / resolution',
      scope: 'Scope',
    },
    calendar: {
      name: 'Name',
      timeZone: 'Time zone',
      timeZoneHelp: 'IANA zone (America/New_York) or "customer" for the customer\'s zone.',
      is24x7: 'Open 24×7',
      hours: 'Business hours',
      hoursHelp: 'Per day, e.g. 09:00-12:00, 13:00-17:00. Empty = closed.',
      holidays: 'Holidays',
      holidaysHelp: 'One YYYY-MM-DD per line.',
      days: { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' },
      summary24x7: '24×7',
      holidayCount: '{{count}} holidays',
    },
    rule: {
      name: 'Name',
      order: 'Order',
      conditions: 'Conditions (JSON)',
      actions: 'Actions (JSON)',
    },
    auditCols: { at: 'When', entity: 'Entity', action: 'Action', actor: 'Actor', reason: 'Reason' },
    errors: {
      required: 'Required.',
      positiveMinutes: 'A whole number of minutes above 0.',
      firstAfterResolution: 'First response must not exceed resolution.',
      percent: 'A whole percentage between 1 and 99.',
      timeZone: 'Not a known time zone.',
      hours: 'Use HH:MM-HH:MM ranges, separated by commas.',
      noHours: 'Open at least one day, or choose 24×7.',
      holidays: 'Dates must be YYYY-MM-DD.',
      json: 'Must be a JSON object or array.',
      order: 'A whole number.',
    },
  },
  login: {
    title: 'Sign in to Support Desk',
    devSubtitle: 'Development sign-in: no password required.',
    subtitle: 'Use your Title21 account.',
    email: 'Email',
    username: 'Email',
    password: 'Password',
    submit: 'Sign in',
    submitting: 'Signing in…',
    quickPick: 'Quick pick',
    failed: 'Sign-in failed',
    newPasswordRequired: 'Your account needs a new password. Set it in the Title21 sign-in page first.',
  },
} as const
